"use client";

import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useEscapeLayer, useFocusTrap, useHotkey, useMounted, useScrollLock, useScrollIntoView } from "./hooks.ts";
import { CommandIcon, SearchIcon } from "./icons.tsx";

/* ──────────────────────────────────────────────────────────────────────────
   The command palette.

   ⌘K. It is the fastest route to any link, creator or domain in the system,
   and the only way some of the rarer actions are reachable at all, which is
   deliberate: an action used once a month does not deserve a button on a
   screen used every hour.

   Ranking is prefix-first, the same rule the comboboxes use, so the muscle
   memory is shared.
   ────────────────────────────────────────────────────────────────────────── */

export type Command = {
  id: string;
  label: string;
  hint?: string;
  group: string;
  icon?: ReactNode;
  keywords?: string;
  shortcut?: string;
  onSelect: () => void;
};

function rank(commands: Command[], query: string): Command[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return commands.slice(0, 40);
  const scored: { command: Command; score: number }[] = [];
  for (const command of commands) {
    const label = command.label.toLowerCase();
    const hint = (command.hint ?? "").toLowerCase();
    const keywords = (command.keywords ?? "").toLowerCase();
    if (label.startsWith(needle)) scored.push({ command, score: 0 });
    else if (label.includes(needle)) scored.push({ command, score: 1 });
    else if (hint.includes(needle)) scored.push({ command, score: 2 });
    else if (keywords.includes(needle)) scored.push({ command, score: 3 });
    else if (subsequence(label, needle)) scored.push({ command, score: 4 });
  }
  return scored.sort((a, b) => a.score - b.score).slice(0, 40).map((entry) => entry.command);
}

/** "lnkedt" finds "Link editor". Cheap, and forgiving of typos in a hurry. */
function subsequence(haystack: string, needle: string): boolean {
  let index = 0;
  for (const char of haystack) {
    if (char === needle[index]) index += 1;
    if (index === needle.length) return true;
  }
  return false;
}

export function CommandPalette({
  commands,
  open,
  onOpenChange,
  placeholder = "Search links, creators, domains, actions…",
}: {
  commands: Command[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  placeholder?: string;
}) {
  const [query, setQuery] = useState("");
  const [cursor, setCursor] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const mounted = useMounted();

  useHotkey({ key: "k", meta: true, inFields: true }, () => onOpenChange(!open));
  useFocusTrap(panelRef, open);
  useScrollLock(open);
  useEscapeLayer(open, () => onOpenChange(false));

  const matches = useMemo(() => rank(commands, query), [commands, query]);
  useEffect(() => setCursor(0), [query]);
  // Cleared on both transitions, not only on open: leaving the last search in
  // place means the next ⌘K starts with text somebody has to notice and delete.
  useEffect(() => {
    setQuery("");
    setCursor(0);
  }, [open]);
  useScrollIntoView(listRef, cursor, open);

  if (!mounted || !open) return null;

  const run = (index: number) => {
    const command = matches[index];
    if (!command) return;
    onOpenChange(false);
    // After the palette is gone, so a command that opens a dialog does not
    // fight the palette's own focus restore.
    requestAnimationFrame(() => command.onSelect());
  };

  const groups: ReactNode[] = [];
  let lastGroup: string | null = null;
  matches.forEach((command, index) => {
    if (command.group !== lastGroup) {
      lastGroup = command.group;
      groups.push(
        <span className="pop-section" key={`g-${command.group}-${index}`}>
          {command.group}
        </span>
      );
    }
    groups.push(
      <button
        key={command.id}
        type="button"
        role="option"
        aria-selected={index === cursor}
        className={`palette-row${index === cursor ? " cursor" : ""}`}
        onPointerEnter={() => setCursor(index)}
        onClick={() => run(index)}
      >
        <span className="mark">{command.icon ?? <CommandIcon size={13} />}</span>
        <span className="palette-copy">
          <strong>{command.label}</strong>
          {command.hint ? <small>{command.hint}</small> : null}
        </span>
        {command.shortcut ? <kbd className="key">{command.shortcut}</kbd> : <span />}
      </button>
    );
  });

  return createPortal(
    <div
      className="palette-backdrop"
      onPointerDown={(event) => {
        if (event.target === event.currentTarget) onOpenChange(false);
      }}
    >
      <div
        className="palette"
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        onPointerDown={(event) => event.stopPropagation()}
      >
        <div className="palette-input">
          <span style={{ display: "grid", placeItems: "center" }}>
            <SearchIcon size={16} />
          </span>
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={placeholder}
            aria-label="Search commands"
            aria-controls="palette-list"
            aria-activedescendant={matches[cursor]?.id}
            autoComplete="off"
            spellCheck={false}
            data-autofocus
            onKeyDown={(event) => {
              if (event.key === "ArrowDown") {
                event.preventDefault();
                setCursor((index) => (matches.length === 0 ? 0 : (index + 1) % matches.length));
              } else if (event.key === "ArrowUp") {
                event.preventDefault();
                setCursor((index) => (matches.length === 0 ? 0 : (index - 1 + matches.length) % matches.length));
              } else if (event.key === "Enter") {
                event.preventDefault();
                run(cursor);
              } else if (event.key === "Escape") {
                event.preventDefault();
                onOpenChange(false);
              }
            }}
          />
          <kbd className="key">esc</kbd>
        </div>

        <div className="palette-list" id="palette-list" role="listbox" ref={listRef} aria-label="Commands">
          {matches.length === 0 ? (
            <div className="panel-empty" style={{ padding: "30px 20px" }}>
              <strong>Nothing matches “{query}”</strong>
              <p>Try a creator name, a hostname or part of a slug.</p>
            </div>
          ) : (
            groups
          )}
        </div>

        <div className="palette-foot">
          <span className="row">
            <kbd className="key">↑</kbd>
            <kbd className="key">↓</kbd> move
          </span>
          <span className="row">
            <kbd className="key">↵</kbd> open
          </span>
          <span className="spacer" />
          <span>{matches.length} result{matches.length === 1 ? "" : "s"}</span>
        </div>
      </div>
    </div>,
    document.body
  );
}
